/**
 * text-command-service.js — the parent-side seam for text formatting.
 *
 * ── The correction this module exists to encode ───────────────────────────
 * The plan's first draft said row 2's text controls would call
 * `editing/format-engine.js`. They cannot. That module is INJECTED INTO THE
 * IFRAME by `iframe-bridge.js` via `generateFormatEngineScript()`; no parent
 * module imports its functions and none can. The real parent seam is
 * `editing/text-editor.js` `applyFormatCommand(command, value)`, which posts
 * `format-text` over the bridge and already owns the bulk-mode branch.
 *
 * ── Two different write paths, and they are not interchangeable ───────────
 *   INLINE marks  — bold, italic, colour, font family, link — travel over the
 *                   bridge to the format engine and act on the SELECTION.
 *   BLOCK styles  — font size, alignment — are ELEMENT OVERRIDES written with
 *                   `applyElementOverrides`, scoped by
 *                   `getResponsiveOverrideViewport()`, and act on the whole
 *                   editable.
 *
 * `glass-panel-text.js` already does exactly this split (`data-prop="fontSize"`
 * / `"textAlign"` go through `applyElementOverride`; bold/italic go through the
 * bridge). Row 2 must agree with it, so both now come through here.
 *
 * A caller that treated size as a format command would get silence: the iframe's
 * `format-text` handler has no branch for it.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { applyElementOverrides } from '../../editing/section-utils.js';
import { getResponsiveOverrideViewport } from '../viewport-controls.js';

/**
 * ── Why the text editor is INJECTED rather than imported ──────────────────
 * `editing/text-editor.js` transitively pulls in `editing/link-popover.js`,
 * which calls `window.addEventListener` at MODULE SCOPE. Importing it makes
 * this file unloadable under node, and an untestable service is how the
 * initiative's own gate #5 ("no command-bar module imports shell.js") gets
 * satisfied in letter while the same problem arrives through a different door.
 *
 * So the seam is injected, exactly as `editing/collab-guard.js` does it: a
 * no-op default, the real implementation wired once at boot. That also makes
 * the dispatch assertable — a test can check WHICH command was sent, which a
 * direct import into a browser-only module never could.
 */
const NOOP_EDITOR = {
  applyFormatCommand: () => {},
  getLatestSelectionState: () => null,
  isBulkFormattingEnabled: () => false
};

let _editor = NOOP_EDITOR;

/**
 * Install the real text editor. `ui/shell.js` calls this at boot; tests inject
 * a spy. Passing null restores the no-op default.
 * @param {{applyFormatCommand: Function, getLatestSelectionState: Function,
 *          isBulkFormattingEnabled: Function}|null} impl
 */
export function setTextEditorImpl(impl) {
  _editor = impl || NOOP_EDITOR;
}

/** Commands the iframe's `format-text` handler actually implements. */
export const INLINE_COMMANDS = Object.freeze([
  'bold',
  'italic',
  'textColor',
  'fontFamily',
  'link',
  'removeLink'
]);

/** Element-override properties the text panel writes. NOT format commands. */
export const BLOCK_PROPS = Object.freeze(['fontSize', 'textAlign']);

/**
 * Apply an inline mark to the current selection.
 *
 * A collapsed caret is fine — the iframe expands to the surrounding word via
 * `expandRangeToWord`. What is NOT fine is no active editable at all: the
 * handler opens with `if (!activeEditable) break;` and the command vanishes
 * silently. Callers gate on `canFormat()` first.
 *
 * @param {string} command — one of INLINE_COMMANDS
 * @param {string|object} [value]
 * @returns {boolean} whether the command was dispatched
 */
export function applyInline(command, value = '') {
  if (!INLINE_COMMANDS.includes(command)) return false;
  _editor.applyFormatCommand(command, value);
  return true;
}

/**
 * Write a block-level typography override on the editable itself.
 *
 * @param {string} instanceId
 * @param {string} eid
 * @param {'fontSize'|'textAlign'} prop
 * @param {string} value — '' clears the override
 * @param {{label?: string}} [opts]
 * @returns {boolean}
 */
export function applyBlockProp(instanceId, eid, prop, value, opts = {}) {
  if (!instanceId || !eid || !BLOCK_PROPS.includes(prop)) return false;
  return applyElementOverrides(
    instanceId,
    eid,
    { [prop]: value },
    {
      // fontSize is per-breakpoint; alignment is not — the same rule the text
      // panel applies (`prop === 'fontSize' ? getResponsiveOverrideViewport() : null`).
      viewport: prop === 'fontSize' ? getResponsiveOverrideViewport() : null,
      label: opts.label || (prop === 'fontSize' ? 'Change text size' : 'Change text alignment'),
      source: 'command-bar'
    }
  );
}

/**
 * Bracket a scrub (a held stepper, a dragged slider) so the whole run collapses
 * into ONE history entry instead of one per frame.
 *
 * This is a pair of explicit calls rather than a `transaction: true` option on
 * `applyBlockProp`, because `applyElementOverridesAcross` does not accept one —
 * it takes `viewport`, `label`, `source` and `noFanOut`, and an unrecognised key
 * would be silently discarded. An option that looks like it batches and does
 * not is worse than no option: every keypress would land as its own undo step
 * and nothing would say so. `glass-panel-text.js` brackets its own nudges the
 * same way (`store.beginTransaction('Nudge text size')`).
 *
 * @example
 *   beginScrub('Nudge text size');
 *   … repeated applyBlockProp(…) …
 *   endScrub();
 */
export function beginScrub(label) {
  store.beginTransaction(label || 'Adjust text');
}

export function endScrub() {
  store.commitTransaction();
}

/**
 * The live selection state the iframe last reported: `{ bold, italic, textColor,
 * fontSlug, link, hasSelection, computedFontSize, computedFontWeight, … }`, or
 * null when nothing is being edited.
 *
 * This is what drives a pressed/unpressed button. Reading it from anywhere else
 * means two surfaces can disagree about whether the caret is inside bold text.
 */
export function textFormatState() {
  return _editor.getLatestSelectionState();
}

/** True when an inline command would actually land. */
export function canFormat() {
  return _editor.getLatestSelectionState() !== null;
}

/** True while the panel's bulk checkbox is on — the command hits every matching editable. */
export function isBulkMode() {
  return _editor.isBulkFormattingEnabled();
}

/**
 * Subscribe to selection-state changes. Fires with the state object or null.
 * `editing:selection-state` is emitted by `text-editor.js` on every selection
 * change AND on clear, so a subscriber never has to guess at the cleared case.
 *
 * @param {(state: object|null) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onTextFormatStateChange(fn) {
  if (typeof fn !== 'function') return () => {};
  return eventBus.on('editing:selection-state', fn);
}
